import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, FilePlus, BarChart2, Zap, ChevronRight, AlertCircle } from 'lucide-react';
import { getModelos, getExecucoes, getUnidades } from '../services/api';
import { getChecklistUserName, setChecklistUserName } from '../utils/userStorage';
import { useAuthSession } from '../../../../contexts/AuthSessionContext';

const KpiCard = ({ title, value, icon, colorClass }) => (
  <div className="bg-white p-5 rounded-lg shadow-sm flex items-center space-x-4 min-w-0">
    <div className={`p-3 rounded-full ${colorClass}`}>{icon}</div>
    <div className="min-w-0">
      <p className="text-sm text-gray-500 truncate">{title}</p>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
    </div>
  </div>
);

const ActionCard = ({ title, description, icon, onClick }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className="flex items-center w-full text-left p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
  >
    {icon}
    <div className="ml-4 flex-grow min-w-0">
      <p className="font-semibold text-gray-700 truncate">{title}</p>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
    <ChevronRight className="h-5 w-5 text-gray-400" />
  </motion.button>
);

const Eye = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const Dashboard = () => {
  const [kpiData, setKpiData] = useState(null);
  const [ultimasExecucoes, setUltimasExecucoes] = useState([]);
  const [execucoesEmAndamento, setExecucoesEmAndamento] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const {
    user,
    isOwner,
    cheflistAdmin,
  } = useAuthSession();

  const userName = (() => {
    const rawName = user?.username || user?.name || getChecklistUserName();
    const normalizedName = typeof rawName === 'string' ? rawName.trim() : String(rawName || '').trim();
    return normalizedName || 'Usuário';
  })();

  useEffect(() => {
    if (userName) {
      setChecklistUserName(userName);
    }
  }, [userName]);

  useEffect(() => {
    (async () => {
      try {
        const [modelos, execucoes, unidades] = await Promise.all([
          getModelos(),
          getExecucoes(),
          getUnidades(),
        ]);

        const pendentes = execucoes
          .filter((execucao) => execucao.status !== 'CONCLUIDO')
          .sort((a, b) => new Date(b.iniciado_em) - new Date(a.iniciado_em));
        setExecucoesEmAndamento(pendentes);

        const ordenadas = execucoes
          .filter((execucao) => execucao.status === 'CONCLUIDO')
          .sort((a, b) => new Date(b.concluido_em || b.iniciado_em) - new Date(a.concluido_em || a.iniciado_em));
        setUltimasExecucoes(ordenadas.slice(0, 5));

        setKpiData({
          modelosCriados: modelos.length,
          checklistsPendentes: pendentes.length,
          unidadesCriadas: unidades.length,
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleNavigation = (path) => navigate(path);

  if (loading) {
    return <div className="p-6">Carregando...</div>;
  }

  if (error) {
    return <div className="p-6">Erro: {error}</div>;
  }

  if (!kpiData) return null;

  const temAndamento = execucoesEmAndamento.length > 0;
  const temHistorico = ultimasExecucoes.length > 0;
  const canManageChecklist = isOwner || cheflistAdmin;

  return (
    <div className="p-4 sm:p-6">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">
        Olá, {userName}
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {canManageChecklist && (
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm p-5 sm:p-6">
            <h2 className="font-bold text-gray-800 mb-4 text-lg">Ações Rápidas</h2>
            <div className="space-y-4">
              <ActionCard
                title="Criar um Modelo de Checklist"
                description="Construa modelos para as suas auditorias e inspeções."
                icon={<FilePlus className="h-8 w-8 text-purple-500" />}
                onClick={() => handleNavigation('/checklist/modelos/novo')}
              />
              {isOwner && (
                <ActionCard
                  title="Criar nova unidade"
                  description="Cadastre aqui uma nova unidade."
                  icon={<Home className="h-8 w-8 text-green-500" />}
                  onClick={() => handleNavigation('/unidades/novo')}
                />
              )}
            </div>
          </div>
        )}

        <div className={canManageChecklist ? '' : 'lg:col-span-3'}>
          <div className="bg-purple-600 text-white rounded-lg shadow-sm p-6 flex flex-col items-center text-center h-full justify-center">
            <Zap className="h-10 w-10 mb-3" />
            <h2 className="font-bold text-xl mb-2">Aplique um Checklist!</h2>
            <p className="text-sm text-purple-100 mb-4">
              Comece a usar a principal funcionalidade da plataforma agora mesmo.
            </p>
            <button
              onClick={() => handleNavigation('/checklist/aplicar')}
              className="w-full bg-white text-purple-600 font-bold py-2 px-4 rounded-lg hover:bg-purple-50 transition-colors"
            >
              Aplicar Checklist
            </button>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Atividade</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          <KpiCard
            title="Checklists Pendentes"
            value={kpiData.checklistsPendentes}
            icon={<Home className="h-6 w-6 text-yellow-800" />}
            colorClass="bg-yellow-100"
          />
          <KpiCard
            title="Modelos Criados"
            value={kpiData.modelosCriados}
            icon={<FilePlus className="h-6 w-6 text-purple-800" />}
            colorClass="bg-purple-100"
          />
          <KpiCard
            title="Unidades Criadas"
            value={kpiData.unidadesCriadas}
            icon={<BarChart2 className="h-6 w-6 text-green-800" />}
            colorClass="bg-green-100"
          />
        </div>
      </div>

      {temAndamento && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Checklists em andamento</h2>
          <div className="hidden sm:block overflow-x-auto bg-white rounded-lg shadow-sm border border-gray-100">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left font-semibold text-gray-500 uppercase tracking-wider">Checklist</th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-500 uppercase tracking-wider">Unidade</th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-500 uppercase tracking-wider">Iniciado em</th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-center font-semibold text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {execucoesEmAndamento.map((execucao) => (
                  <tr key={execucao.execucao_id}>
                    <td className="px-6 py-4 font-medium">{execucao.checklist_modelos?.titulo}</td>
                    <td className="px-6 py-4">{execucao.unidades?.nome}</td>
                    <td className="px-6 py-4">{new Date(execucao.iniciado_em).toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        Em andamento
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => navigate(`/checklist/execucoes/${execucao.execucao_id}`)}
                        className="bg-purple-600 hover:bg-purple-700 text-white p-2 rounded-md transition-colors"
                        title="Continuar"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {temHistorico && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Últimos checklists aplicados</h2>
          <div className="hidden sm:block overflow-x-auto bg-white rounded-lg shadow-sm border border-gray-100">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left font-semibold text-gray-500 uppercase tracking-wider">Checklist</th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-500 uppercase tracking-wider">Unidade</th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-500 uppercase tracking-wider">Data</th>
                  <th className="px-6 py-3 text-left font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-center font-semibold text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ultimasExecucoes.map((execucao) => (
                  <tr key={execucao.execucao_id}>
                    <td className="px-6 py-4 font-medium">{execucao.checklist_modelos?.titulo}</td>
                    <td className="px-6 py-4">{execucao.unidades?.nome}</td>
                    <td className="px-6 py-4">{new Date(execucao.concluido_em || execucao.iniciado_em).toLocaleString()}</td>
                    <td className="px-6 py-4">
                      {execucao.status === 'CONCLUIDO' ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Concluído
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Em andamento
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => navigate(`/checklist/execucoes/${execucao.execucao_id}/detalhes`)}
                        className="bg-purple-600 hover:bg-purple-700 text-white p-2 rounded-md transition-colors"
                        title="Visualizar"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {canManageChecklist && (
            <div className="mt-4 flex items-center">
              <AlertCircle className="h-4 w-4 text-yellow-500 mr-2" />
              <p className="text-sm text-gray-600">
                Para ver todos os checklists, acesse{' '}
                <Link to="/checklist/execucoes" className="text-purple-600 hover:underline">execuções</Link>.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
